<script>
export const BS_EVENTS = ['show.bs.modal', 'shown.bs.modal', 'hide.bs.modal', 'hidden.bs.modal', 'hidePrevented.bs.modal'];
export const BS_METHODS = ['show', 'hide', 'handleUpdate', 'toggle'];
</script>

<script setup>
import $ from 'jquery';

import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { v4 as uuidv4 } from 'uuid';

import i18n from './DsModal.i18n.js';

const props = defineProps({
    title: {
        type: String,
        required: false,
        default: null,
    },
    closeButton: {
        type: Boolean,
        required: false,
        default: true,
    },
    fade: {
        type: Boolean,
        required: false,
        default: true,
    },
    size: {
        type: String,
        required: false,
        default: '',
        validator: (x) => ['sm', '', 'lg', 'xl'].includes(x),
    },
    backdrop: {
        type: [String, Boolean],
        required: false,
        default: true,
        validator: (x) => x === 'static' || typeof x === 'boolean',
    },
    keyboard: {
        type: Boolean,
        required: false,
        default: true,
    },
    focus: {
        type: Boolean,
        required: false,
        default: true,
    },
    show: {
        type: Boolean,
        required: false,
        default: true,
    },
    classFooter: {
        type: [String, Array, Object],
        required: false,
        default: '',
    },
    isForm: {
        type: Boolean,
        required: false,
        default: false,
    },
    useDefaultBody: {
        type: Boolean,
        required: false,
        default: true,
    },
    renderOnlyWhenOpen: {
        type: Boolean,
        default: false,
    },
    closeButtonClickHandler: {
        type: Function,
        required: false,
        default: null
    },
    bodyBackgroundColor: {
        type: String,
        required: false,
        default: '',
        validator: (x) => ['', 'bg-surface-1', 'bg-surface-2', 'bg-surface-3'].includes(x),
    },
});


const emit = defineEmits([
    ...BS_EVENTS
]);

const root = ref(null);
const isOpen = ref(props.show);
const renderRoot = ref(props.renderOnlyWhenOpen ? props.show : true);

const titleId = props.title ? `ds-modal-title-${uuidv4()}` : null;

const dialogClasses = computed(() => {
    const res = [];
    if (props.size !== '') {
        res.push(`modal-${props.size}`);
    }
    return res;
});

const allEvents = BS_EVENTS.join(" ");
watch(root, (newRoot, oldRoot) => {
    if (oldRoot && oldRoot !== newRoot) {
        $(root.value)
            .modal('dispose')
            .off(allEvents, eventHandler);
    }
    if (newRoot && newRoot !== oldRoot) {
        $(root.value)
            .on(allEvents, eventHandler)
            .modal({
                backdrop: props.backdrop,
                keyboard: props.keyboard,
                focus: props.focus,
                show: props.show,
            });
    }
});
onBeforeUnmount(() => {
    if (root.value) {
        if (isOpen.value) {
            // Remove 'fade' class from modal element to force the modal to
            // close immediately without any animations/transitions.
            root.value.classList.remove('fade');
            $(root.value).modal('hide');
        }
        $(root.value)
            .modal('dispose')
            .off(allEvents, eventHandler);
    }
});

function eventHandler(event) {
    if (event.namespace !== "bs.modal") {
        return;
    }
    if (event.type === 'show') {
        isOpen.value = true;
    } else if (event.type === 'hidden') {
        isOpen.value = false;
    }
    emit(`${event.type}.${event.namespace}`, event);
}

watch(isOpen, () => {
    const wrapper = document.querySelector('.site-wrapper');
    if (wrapper) {
        wrapper.inert = isOpen.value;
    }
}, {
    immediate: true,
    flush: 'sync',
});

const methods = {};
for (const name of BS_METHODS) {
    methods[name] = () => $(root.value).modal(name);
}

if (props.renderOnlyWhenOpen) {
    const originalShow = methods.show;
    methods.show = async () => {
        renderRoot.value = true;
        await nextTick();
        originalShow();
    };
    watch(isOpen, () => {
        if (!isOpen.value) {
            renderRoot.value = false;
        }
    });
}

function hidden() {
    return new Promise(resolve => {
        const stopWatching = watch(isOpen, () => {
            if (!isOpen.value) {
                stopWatching();
                resolve();
            }
        });
        methods.hide();
    });
}

defineExpose({
    ...methods,
    getElement() {
        return root;
    },
    hidden,
    replaceWith(newModal) {
        hidden().then(() => newModal.show());
    }
});
</script>

<template>
    <Teleport to="body">
        <div
            v-if="renderRoot"
            class="modal"
            :class="{ 'fade': fade }"
            tabindex="-1"
            ref="root"
            :aria-labelledby="titleId"
            v-bind="$attrs"
        >
            <div class="modal-dialog" :class="dialogClasses">
                <Component class="modal-content" :class="{ 'overflow-hidden': !!bodyBackgroundColor }" :is="isForm ? 'form' : 'div'">
                    <div class="modal-header" v-if="title || closeButton">
                        <h5 :id="titleId" class="modal-title" v-if="title">
                            {{ title }}
                        </h5>
                        <button
                            type="button"
                            class="close"
                            :aria-label="i18n.close()"
                            @click.prevent="closeButtonClickHandler ? closeButtonClickHandler() : methods.hide()"
                            v-if="closeButton"
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <slot v-if="!useDefaultBody" />
                    <div class="modal-body" :class="bodyBackgroundColor" v-else>
                        <slot />
                    </div>
                    <div class="modal-footer" :class="classFooter" v-if="$slots.footer">
                        <slot name="footer" />
                    </div>
                </Component>
            </div>
        </div>
    </Teleport>
</template>
